import {
  getImageOptions,
  snackMenuItemsOptions,
  snackSupplierOptions,
  useBuilding,
} from "@/api";
import { SnackCategory, SnackMenuItem } from "@/types";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styles from "@/styles/Snack.module.scss";
import {
  Button,
  Checkbox,
  Chip,
  DropdownMenu,
  Icon,
  InputField,
} from "@app-components";
import { AnimatePresence, motion } from "framer-motion";
import Table from "@/components/table/Table";
import { createColumnHelper, RowSelectionState } from "@tanstack/react-table";
import { NOK } from "@/lib";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import { useWelcomeSnackAccess } from "@/hooks/useWelcomeSnackAccess";
import { useRouterStore } from "@/store/zustandStore";
import { useBuildingQuery } from "@/components/RouterSetup";
import { DEFAULT_CUTOFF_SETTINGS } from "@/lib/snackCutoff";
import { CutoffCell, PortalTooltip } from "@/components/snack/CutoffCell";
import ItemsCutoffModal from "@/components/snack/ItemsCutoffModal";

const columnHelper = createColumnHelper<SnackMenuItem>();

export const Route = createFileRoute("/_portal/snack-admin/menu/")({
  component: RouteComponent,
  loader: () => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.prefetchQuery(snackSupplierOptions(supplierCompanyId));
    queryClient.prefetchQuery(snackMenuItemsOptions(supplierCompanyId, true));
  },
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const { allowSnackAdminAccess } = useWelcomeSnackAccess();
  const navigate = useNavigate();
  const router = useRouter();
  const { data: building } = useBuilding();
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building?.supplierCompanyId),
  );
  const { data: menu, isLoading } = useBuildingQuery(
    snackMenuItemsOptions(building.supplierCompanyId, true),
  );
  const allCategories = useMemo(
    () =>
      new Map<string, SnackCategory>(menu?.categories?.map((c) => [c.id, c])),
    [menu],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [filterOnCategory, setFilterOnCategory] = useState<string | null>(null);
  const [showCategoryBackButton, setShowCategoryBackButton] = useState(false);
  const [showCategoryForwardButton, setShowCategoryForwardButton] =
    useState(false);
  const [filteredMenuItems, setFilteredMenuItems] = useState<SnackMenuItem[]>(
    menu?.items ?? [],
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [cutoffModalOpen, setCutoffModalOpen] = useState(false);

  // Selection survives search and category filtering, so resolve it against
  // the whole menu rather than the visible rows
  const selectedItemIds = useMemo(
    () =>
      (menu?.items ?? [])
        .filter((item) => rowSelection[item.id])
        .map((item) => item.id),
    [menu?.items, rowSelection],
  );

  const categoryButtonRefs = useRef<{
    [key: string]: HTMLButtonElement | null;
  }>({});
  const categoriesScrollRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!searchTerm.trim() && !filterOnCategory) {
      setFilteredMenuItems(menu?.items || []);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const filteredItems = (menu?.items ?? []).filter(
      (item) =>
        (!searchTerm ||
          item.name[i18n.language === "en" ? "en" : "no"]
            .toLowerCase()
            .includes(lowerSearchTerm)) &&
        (!filterOnCategory || item.categoryId === filterOnCategory),
    );
    setFilteredMenuItems(filteredItems);
  }, [searchTerm, filterOnCategory, menu]);

  // Function to update arrow button visibility
  const updateArrowButtonVisibility = useCallback(() => {
    const scrollContainer = categoriesScrollRowRef.current;
    if (!scrollContainer) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;

    // Show back button if scrolled to the right (scrollLeft > 10)
    setShowCategoryBackButton(scrollLeft > 10);

    // Show forward button if there's more content to scroll (not at the end)
    setShowCategoryForwardButton(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  // Function to scroll categories horizontally
  const scrollCategories = useCallback((direction: "left" | "right") => {
    const scrollContainer = categoriesScrollRowRef.current;
    if (!scrollContainer) return;

    const scrollAmount = scrollContainer.clientWidth * 0.7; // Scroll 70% of container width
    const newScrollLeft =
      direction === "left"
        ? Math.max(0, scrollContainer.scrollLeft - scrollAmount)
        : scrollContainer.scrollLeft + scrollAmount;

    scrollContainer.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  }, []);

  // Ref callback to store category button refs
  const setCategoryButtonRef = useCallback(
    (category: string) => (el: HTMLButtonElement | null) => {
      categoryButtonRefs.current[category] = el;
    },
    [],
  );

  // Set up scroll listener for categories buttons row container
  useEffect(() => {
    const scrollContainer = categoriesScrollRowRef.current;
    if (!scrollContainer) return;

    // Initial check
    updateArrowButtonVisibility();

    // Add scroll listener
    scrollContainer.addEventListener("scroll", updateArrowButtonVisibility);

    // Add resize listener to handle container size changes
    const resizeObserver = new ResizeObserver(updateArrowButtonVisibility);
    resizeObserver.observe(scrollContainer);

    return () => {
      scrollContainer.removeEventListener(
        "scroll",
        updateArrowButtonVisibility,
      );
      resizeObserver.disconnect();
    };
  }, [updateArrowButtonVisibility]);

  // Update arrow visibility when filtered categories change
  useEffect(() => {
    // Small delay to let DOM update
    const timer = setTimeout(updateArrowButtonVisibility, 0);
    return () => clearTimeout(timer);
  }, [filteredMenuItems, updateArrowButtonVisibility]);

  const restaurantCutoffSettings =
    supplier?.orderingMode === "cutoff-v1"
      ? (supplier.cutoffSettings ?? DEFAULT_CUTOFF_SETTINGS)
      : undefined;
  const closedOnWeekends = supplier?.closedOnWeekends;

  // Cut-off is the only bulk action, so selection is pointless without it
  const enableSelection = !!restaurantCutoffSettings;

  const columns = useMemo(
    () => [
      ...(enableSelection
        ? [
            columnHelper.display({
              id: "selectCol",
              cell: ({ row }) => (
                <Checkbox
                  label="Select row"
                  hideLabel
                  checked={row.getIsSelected()}
                  onChange={row.getToggleSelectedHandler()}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    container: { display: "unset" },
                    checkbox: { top: "unset" },
                  }}
                />
              ),
              header: ({ table }) => (
                <Checkbox
                  label="Select all"
                  hideLabel
                  checked={table.getIsAllPageRowsSelected()}
                  nativeOnChange={table.getToggleAllPageRowsSelectedHandler()}
                  style={{
                    container: { display: "unset" },
                    checkbox: { top: "unset" },
                  }}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor("name", {
        cell: (info) => info.getValue()[i18n.language === "en" ? "en" : "no"],
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("SNACK_ADMIN.MENU.TABLE.NAME")}
          />
        ),
        sortingFn: (rowA, rowB) => {
          return rowA.original.name[
            i18n.language === "en" ? "en" : "no"
          ].localeCompare(
            rowB.original.name[i18n.language === "en" ? "en" : "no"],
          );
        },
      }),
      columnHelper.accessor(
        (row) =>
          allCategories.get(row.categoryId)?.name[
            i18n.language === "en" ? "en" : "no"
          ] || null,
        {
          id: "category",
          cell: (info) => info.getValue(),
          header: (info) => (
            <SortableTableHeader
              info={info}
              label={t("SNACK_ADMIN.MENU.TABLE.CATEGORY")}
            />
          ),
        },
      ),
      columnHelper.accessor("price", {
        cell: (info) => NOK.format(info.getValue()),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={`${t("SNACK_ADMIN.MENU.TABLE.PRICE")} (${t("EXCLUDING_VAT")})`}
          />
        ),
        meta: {
          style: {
            textAlign: "right",
            display: "flex",
            justifyContent: "end",
            paddingRight: "var(--wlcm-spacing-lg)",
          },
        },
      }),
      columnHelper.accessor("imageUrl", {
        cell: (info) => {
          const value = info.getValue();
          return <ImageUrlCell value={value[0]} />;
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={
              <span className="sr-only">
                {t("SNACK_ADMIN.MENU.TABLE.IMAGE")}
              </span>
            }
          />
        ),
        sortUndefined: "last",
      }),
      columnHelper.accessor("isAvailable", {
        cell: (info) =>
          info.getValue() ? (
            <></>
          ) : (
            <>
              <Icon
                name="visibility_off"
                className="dangerIcon"
                style={{ verticalAlign: "middle" }}
              />
              <PortalTooltip anchorSelect=".dangerIcon">
                <Trans
                  i18nKey="SNACK_ADMIN.MENU.ITEM.IS_NOT_AVAILABLE"
                  components={{
                    bold: (
                      <strong
                        style={{ color: "var(--wlcm-color-danger-red)" }}
                      ></strong>
                    ),
                  }}
                />
              </PortalTooltip>
            </>
          ),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={
              <span className="sr-only">
                {t("SNACK_ADMIN.MENU.ITEM.SHOULD_BE_AVAILABLE")}
              </span>
            }
          />
        ),
      }),
      ...(restaurantCutoffSettings
        ? [
            columnHelper.accessor(
              (row) => row.cutoffSettings ?? restaurantCutoffSettings,
              {
                id: "cutoff",
                cell: (info) => (
                  <CutoffCell
                    itemId={info.row.original.id}
                    settings={info.getValue()}
                    isOverride={!!info.row.original.cutoffSettings}
                    closedOnWeekends={closedOnWeekends}
                  />
                ),
                header: (info) => (
                  <SortableTableHeader
                    info={info}
                    label={t("SNACK_ADMIN.MENU.TABLE.CUTOFF")}
                  />
                ),
                sortingFn: (rowA, rowB) =>
                  Number(!!rowA.original.cutoffSettings) -
                  Number(!!rowB.original.cutoffSettings),
              },
            ),
          ]
        : []),
      columnHelper.accessor((row) => [row.id, row.allergens], {
        id: "allergens",
        cell: (info) => {
          const [id, allergens] = info.getValue() as [
            string,
            string[] | undefined,
          ];
          const cleanAllergens = (allergens || []).filter(Boolean);
          const restCount = cleanAllergens.length - 1;
          return cleanAllergens.length > 0 ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--wlcm-spacing-xs)",
              }}
            >
              {cleanAllergens.slice(0, 1).map((allergen) => (
                <Chip
                  key={allergen}
                  label={t(`ALLERGENS.${allergen}.LABEL`)}
                  className="tableTag"
                  style={{ textTransform: "capitalize" }}
                />
              ))}
              {restCount > 0 && (
                <>
                  <strong style={{ opacity: 0.5 }} id={`rest-count-${id}`}>
                    + {restCount}
                  </strong>
                  <PortalTooltip
                    anchorSelect={`#rest-count-${id}`}
                    content={cleanAllergens
                      .slice(1)
                      .map((allergen) => t(`ALLERGENS.${allergen}.LABEL`))
                      .join(", ")}
                  />
                </>
              )}
            </div>
          ) : (
            "-"
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("SNACK_ADMIN.MENU.TABLE.ALLERGENS")}
          />
        ),
        sortingFn: (rowA, rowB) => {
          return (
            (rowA.original.allergens || []).filter(Boolean).length -
            (rowB.original.allergens || []).filter(Boolean).length
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        cell: () => (
          <div className="rowActions">
            <Icon name="edit" />
          </div>
        ),
        header: () => (
          <div className="sr-only">{t("BUILDING_VISITORS.ACTIONS")}</div>
        ),
      }),
    ],
    [restaurantCutoffSettings, closedOnWeekends, enableSelection],
  );

  if (!allowSnackAdminAccess) return null;
  return (
    <>
      <div className="pageHeader">
        <h1>{t("SNACK_ADMIN.MENU.HEADER")}</h1>
        <p>
          {t("SNACK_ADMIN.MENU.SUBHEADER", {
            supplierName: supplier?.name || building.displayName,
          })}
        </p>
      </div>
      <div className={styles.menuContainer}>
        <div
          className={styles.searchAndCategories}
          style={{ position: "unset" }}
        >
          <InputField
            compact
            icon="search"
            label={t("PAGES.ORDER_FOOD.MENU.SEARCH")}
            hideLabel
            placeholder={t("PAGES.ORDER_FOOD.MENU.SEARCH")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className={styles.categoriesRelativeWrapper}>
            <AnimatePresence>
              {showCategoryBackButton && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className={`${styles.categoryScrollButtonContainer} ${styles.left}`}
                >
                  <Button
                    circular
                    dark
                    icon="arrow_back"
                    onClick={() => scrollCategories("left")}
                    aria-label="Scroll left"
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <div
              ref={categoriesScrollRowRef}
              className={styles.categoriesContainer}
            >
              {Array.from(allCategories.values()).map(
                ({ id: categoryId, name, linkedItemsCount }) => (
                  <button
                    key={categoryId}
                    ref={setCategoryButtonRef(categoryId)}
                    className={
                      filterOnCategory === categoryId ? styles.active : ""
                    }
                    onClick={() => {
                      if (filterOnCategory === categoryId) {
                        setFilterOnCategory(null);
                      } else {
                        setFilterOnCategory(categoryId);
                      }
                    }}
                  >
                    <span style={!linkedItemsCount ? { opacity: 0.5 } : {}}>
                      {name[i18n.language as "en" | "no"]}
                    </span>
                  </button>
                ),
              )}
            </div>
            <AnimatePresence>
              {showCategoryForwardButton && (
                <motion.div
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className={`${styles.categoryScrollButtonContainer} ${styles.right}`}
                >
                  <Button
                    circular
                    dark
                    icon="arrow_forward"
                    onClick={() => scrollCategories("right")}
                    aria-label="Scroll right"
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <AnimatePresence>
            {enableSelection && selectedItemIds.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
              >
                <DropdownMenu
                  id="snack-menu-actions"
                  label={`${t("COMPANY_SETTINGS.ADMINS.ACTIONS")} (${t(
                    "SNACK_ADMIN.MENU.TABLE.CUTOFF_MIXED_COUNT",
                    { count: selectedItemIds.length },
                  ).toLowerCase()})`}
                  menuOptions={[
                    {
                      icon: "schedule",
                      label: t("SNACK_CUTOFF.ITEMS.ACTION_SELECTED"),
                      onSelect: () => setCutoffModalOpen(true),
                    },
                  ]}
                  alignMenu="end"
                />
              </motion.div>
            )}
          </AnimatePresence>
          <DropdownMenu
            id="snack-menu-add"
            activator={<Button icon="add">{t("CREATE")}</Button>}
            menuOptions={[
              {
                icon: "burger",
                label: t("SNACK_ADMIN.MENU.ADD_ITEM"),
                onSelect: () => navigate({ to: "/snack-admin/menu/item/new" }),
              },
              {
                icon: "new_label",
                label: t("SNACK_ADMIN.MENU.ADD_CATEGORY"),
                onSelect: () =>
                  navigate({
                    to: "/snack-admin/menu/categories",
                    search: { create: true },
                  }),
              },
            ]}
            alignMenu="end"
          />
        </div>
        <Table
          name="snack-admin-menu"
          data={filteredMenuItems}
          columns={columns}
          isLoading={isLoading}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          enableRowSelection={enableSelection}
          enableMultiRowSelection={enableSelection}
          onRowClick={(item) =>
            navigate({
              to: "/snack-admin/menu/item/$itemId",
              params: { itemId: item.id },
            })
          }
          onRowHover={(item) =>
            router.preloadRoute({
              to: "/snack-admin/menu/item/$itemId",
              params: { itemId: item.id },
            })
          }
          defaultSorting={[{ id: "name", desc: false }]}
          noDataMessage={
            searchTerm || filterOnCategory
              ? t("PAGES.ORDER_FOOD.MENU.NO_ITEMS.SEARCH")
              : t("PAGES.ORDER_FOOD.MENU.NO_ITEMS.DEFAULT")
          }
        />
      </div>
      <ItemsCutoffModal
        target={
          cutoffModalOpen
            ? {
                key: "selection",
                label: t("SNACK_CUTOFF.ITEMS.SELECTED"),
                itemIds: selectedItemIds,
                source: "selection",
              }
            : null
        }
        onClose={() => setCutoffModalOpen(false)}
        onSuccess={() => setRowSelection({})}
      />
    </>
  );
}

const ImageUrlCell = ({ value }: { value: string }) => {
  const { data: image } = useBuildingQuery(getImageOptions(value));
  const safeUrlAsId = "img-" + (image ?? "").replace(/[^a-zA-Z0-9_-]/g, "");
  return image ? (
    <>
      <Icon
        name="add_photo"
        className="greenIcon"
        id={safeUrlAsId}
        style={{ verticalAlign: "middle" }}
      />
      <PortalTooltip anchorSelect={`#${safeUrlAsId}`}>
        <img src={image} width="60" height="60" />
      </PortalTooltip>
    </>
  ) : (
    <Icon name="no_photo" style={{ verticalAlign: "middle" }} />
  );
};
